import { useEffect, useState } from 'react'
import { isInstalled, isIos } from '../lib/pwa'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED = 'gridlock:install-dismissed'

/**
 * "Put it on your home screen." Android and desktop Chrome fire a real install
 * event; iOS never has, so it gets Share-sheet instructions instead — which is
 * the platform most of a fantasy league will be on.
 */
export default function InstallPrompt() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIos, setShowIos] = useState(false)
  const [gone, setGone] = useState(true)

  useEffect(() => {
    if (isInstalled()) return
    try {
      if (localStorage.getItem(DISMISSED)) return
    } catch { /* private mode — just show it */ }

    setGone(false)

    if (isIos()) {
      setShowIos(true)
      return
    }
    const onPrompt = (e: Event) => {
      e.preventDefault()
      setEvt(e as BeforeInstallPromptEvent)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    return () => window.removeEventListener('beforeinstallprompt', onPrompt)
  }, [])

  const dismiss = () => {
    setGone(true)
    try { localStorage.setItem(DISMISSED, '1') } catch { /* nothing to do */ }
  }

  const install = async () => {
    if (!evt) return
    await evt.prompt()
    await evt.userChoice
    dismiss()
  }

  if (gone) return null
  if (!evt && !showIos) return null

  return (
    <div className="install" role="region" aria-label="Install Gridlock">
      <div className="install-copy">
        <strong>Put Gridlock on your home screen</strong>
        {showIos ? (
          <span>
            Tap <span className="ios-share" aria-hidden="true">􀈂</span> Share,
            then <em>Add to Home Screen</em>. Picks take four minutes from your phone.
          </span>
        ) : (
          <span>Opens full screen, and we can remind you before kickoff.</span>
        )}
      </div>
      <div className="install-actions">
        {evt && <button className="btn btn-primary" onClick={install}>Install</button>}
        <button className="btn btn-ghost" onClick={dismiss}>Not now</button>
      </div>
    </div>
  )
}
